/**
 * AuthContext.js
 *
 * Manages authentication state for the GQG Viewer.
 *
 * - Access token lives in memory only (never localStorage — PHI protection)
 * - Refresh token is an httpOnly cookie — auto-sent by browser, invisible to JS
 * - On app load: silently tries /auth/refresh to restore session from cookie
 * - On 401: tries refresh → retry → if still 401 → logs out
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { createAuthCacheEpochManager } from './authCacheEpoch';
import { resetStudyOpenRequestHandoffs } from './api';

const AuthContext = createContext(null);

// Axios instance for auth calls (no interceptors — avoids circular retry)
const authAxios = axios.create();

export const AuthProvider = ({ children }) => {
  const [user, setUser]           = useState(null);     // null = not logged in
  const [accessToken, setAccessToken] = useState(null);
  const [cacheEpoch, setCacheEpoch] = useState(null);
  const [loading, setLoading]     = useState(true);     // true while restoring session
  const refreshInProgress         = useRef(false);
  const refreshSubscribers        = useRef([]);         // queued requests waiting on refresh
  const authCacheEpochManagerRef  = useRef(null);
  if (!authCacheEpochManagerRef.current) {
    authCacheEpochManagerRef.current = createAuthCacheEpochManager();
  }

  // ── Token management ──────────────────────────────────────────────────────

  const storeToken = useCallback((token, userInfo) => {
    setAccessToken(token);
    setUser(userInfo);
    setCacheEpoch(authCacheEpochManagerRef.current.authenticate(userInfo?.id));
    // Inject into default axios headers for all future requests
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);

  const clearToken = useCallback(() => {
    resetStudyOpenRequestHandoffs();
    setAccessToken(null);
    setUser(null);
    authCacheEpochManagerRef.current.clear();
    setCacheEpoch(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // ── Refresh ───────────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    try {
      const res = await authAxios.post('/auth/refresh', {}, { withCredentials: true });
      const token = res.data.access_token;
      // Decode user info from JWT payload (middle part)
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1]));
      storeToken(token, {
        id:       payload.sub,
        username: payload.username,
        email:    payload.email,
        name:     payload.name,
        roles:    payload.roles,
      });
      return token;
    } catch {
      clearToken();
      return null;
    }
  }, [storeToken, clearToken]);

  // ── On mount: restore session ─────────────────────────────────────────────

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Axios request interceptor: attach Bearer token ────────────────────────

  useEffect(() => {
    const reqId = axios.interceptors.request.use(config => {
      // Only add if we have a token and the request doesn't already have one
      if (accessToken && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(reqId);
  }, [accessToken]);

  // ── Axios response interceptor: auto-refresh on 401 ──────────────────────

  useEffect(() => {
    const resId = axios.interceptors.response.use(
      res => res,
      async error => {
        const original = error.config;

        // Only retry on 401, not on auth endpoints themselves
        if (
          error.response?.status === 401 &&
          !original._retried &&
          !original.url?.includes('/auth/')
        ) {
          original._retried = true;

          if (refreshInProgress.current) {
            // Queue this request until refresh completes
            return new Promise((resolve, reject) => {
              refreshSubscribers.current.push({ resolve, reject, config: original });
            });
          }

          refreshInProgress.current = true;
          const newToken = await refresh();
          refreshInProgress.current = false;

          if (newToken) {
            // Replay queued requests with new token
            refreshSubscribers.current.forEach(({ resolve, config }) => {
              config.headers['Authorization'] = `Bearer ${newToken}`;
              resolve(axios(config));
            });
            refreshSubscribers.current = [];

            // Retry original request
            original.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(original);
          } else {
            // Refresh failed — reject queued requests, redirect to login
            refreshSubscribers.current.forEach(({ reject }) => reject(error));
            refreshSubscribers.current = [];
            clearToken();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(resId);
  }, [refresh, clearToken]);

  // ── Login / Logout ────────────────────────────────────────────────────────

  const login = useCallback(async (username, password) => {
    const res = await authAxios.post('/auth/login', { username, password },
      { withCredentials: true });
    storeToken(res.data.access_token, res.data.user);
    return res.data.user;
  }, [storeToken]);

  const logout = useCallback(async () => {
    try {
      await authAxios.post('/auth/logout', {}, {
        withCredentials: true,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
    } catch { /* ignore */ }
    clearToken();
  }, [accessToken, clearToken]);

  return (
    <AuthContext.Provider value={{ user, accessToken, cacheEpoch, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
